import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, Spinner } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import {
  taxonomyStandardListCatalogQueryOptions,
  useSeedStandardLists,
  type TaxonomyStandardListEntry,
} from '@/entities/taxonomy-list';
import { FormSheet } from '@/shared/ui/FormSheet';

export interface AddStandardListsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Reference lists first — they're the ones nobody wants to hand-type.
 *
 * Catalog category names, matched by value to rank a list, so they stay literal.
 */

/* eslint-disable i18next/no-literal-string --
 * Catalog category names, compared by value to rank a list. A translated one
 * would stop matching and every list would sort last.
 */
const CATEGORY_ORDER = [
  'Reference',
  'Geography',
  'Media buy',
  'Campaign',
  'Creative',
  'Targeting & offer',
];
/* eslint-enable i18next/no-literal-string */

function categoryRank(category: string): number {
  const i = CATEGORY_ORDER.indexOf(category);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

/**
 * Pick which Standard Lists to add.
 *
 * Adding all nineteen was the only option before, which is wrong for an org
 * that wants Countries but has its own channel taxonomy — and it made the
 * button destructive-feeling on a library you'd already curated. Nothing starts
 * selected, for the reason the state itself records; "Select all" is one click
 * away for the take-the-lot case.
 *
 * Entries already in the library are shown rather than hidden, so the catalog
 * reads the same every time and you can see what you've already taken.
 */
export function AddStandardListsModal({ open, onClose }: AddStandardListsModalProps) {
  const { t } = useTranslation('knowledge');
  const query = useQuery({ ...taxonomyStandardListCatalogQueryOptions(), enabled: open });
  const seed = useSeedStandardLists();

  const entries = useMemo(() => query.data ?? [], [query.data]);
  const available = useMemo(() => entries.filter((e) => !e.alreadyAdded), [entries]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Start empty and reset on each open: adding vocabularies to a library is an
  // opt-in choice, and a preselected list makes "add everything" the accidental
  // default. "Select all" is one click away for the take-the-lot case.
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, TaxonomyStandardListEntry[]>();
    for (const e of entries) {
      if (!byCategory.has(e.category)) byCategory.set(e.category, []);
      byCategory.get(e.category)!.push(e);
    }
    return [...byCategory.entries()].sort(
      ([a], [b]) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
    );
  }, [entries]);

  // Functional updaters throughout — rapid ticks batch, and building from a
  // captured `selected` would silently drop all but the last.
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allSelected = available.length > 0 && selected.size === available.length;
  const totalRows = entries
    .filter((e) => selected.has(e.key))
    .reduce((sum, e) => sum + e.rowCount, 0);

  const handleAdd = () => {
    if (selected.size === 0) return;
    seed.mutate([...selected], { onSuccess: onClose });
  };

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.addStandardListsModal.addStandardLists')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.addStandardListsModal.vocabulariesMonksflowMaintainsAddOnes')}
        </p>

        {available.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2.5 border-b border-border-light pb-3">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() =>
                setSelected(allSelected ? new Set() : new Set(available.map((e) => e.key)))
              }
              aria-label={t('taxonomy.addStandardListsModal.selectAllLists')}
            />
            <span className="text-sm font-medium text-foreground">
              {t('taxonomy.addStandardListsModal.selectAllCount', { count: available.length })}
            </span>
          </label>
        )}

        {query.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          grouped.map(([category, group]) => (
            <section key={category} className="flex flex-col gap-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {category}
              </h3>
              <ul className="flex flex-col gap-1">
                {group.map((entry) => (
                  <li key={entry.key}>
                    <label
                      className={`flex items-start gap-2.5 rounded-md border border-border-light p-2.5 ${
                        entry.alreadyAdded
                          ? 'opacity-60'
                          : 'cursor-pointer hover:border-primary hover:bg-accent/40'
                      }`}
                    >
                      {entry.alreadyAdded ? (
                        <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <Checkbox
                          checked={selected.has(entry.key)}
                          onCheckedChange={() => toggle(entry.key)}
                          className="mt-0.5 shrink-0"
                          aria-label={t('taxonomy.addStandardListsModal.addList', {
                            name: entry.name,
                          })}
                        />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{entry.name}</span>
                          <Badge
                            variant="ghost"
                            roundness="full"
                            className="border-border-light text-[10px]"
                          >
                            {t('taxonomy.addStandardListsModal.optionCount', {
                              count: entry.rowCount,
                            })}
                          </Badge>
                          {entry.alreadyAdded && (
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {t('taxonomy.addStandardListsModal.alreadyAdded')}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{entry.description}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: seed.isPending
            ? t('taxonomy.addStandardListsModal.adding')
            : selected.size === 0
              ? t('taxonomy.addStandardListsModal.selectLeastOne')
              : t('taxonomy.addStandardListsModal.addLists', {
                  count: selected.size,
                  options: totalRows,
                }),
          onClick: handleAdd,
          disabled: selected.size === 0 || seed.isPending,
          loading: seed.isPending,
        }}
        secondary={{ label: t('taxonomy.addStandardListsModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

AddStandardListsModal.displayName = 'AddStandardListsModal';
